/** `downhill/ski-2600` — the downhill family template authored from the
 * stored Ski 2600 concept: an Atari-style one-player ski race. The skier
 * holds the upper quarter of a portrait screen while the snowy course
 * scrolls up past them at the pace they ski. Left/right swing the skier's
 * heading (the arc is tighter the slower they run, and traversing costs
 * downhill pace), up tucks for speed, down snowplows to shed it. Pine trees
 * are a wipeout — a tumble of about a second and a half with the clock
 * running, then they get up where they fell and pick up speed again; moguls
 * are a lesser bump that slows and shoves them sideways. Slalom gates (a red
 * and a blue pole) must be threaded: each miss adds two seconds to the race
 * time. The course is three fixed sections with distinct rhythms — an open
 * speed field opening on the first decision (a narrow, straight gap between
 * two pines or the wide sweep around them), a tight tree corridor that
 * punishes over-tucking, and a mogul field whose gates sit just past the
 * bumps — after an obstacle-free drop-in; the seed shuffles the trees and
 * moguls within each section, never onto a gate's line. The finish banner
 * stops the clock; every second under par scores, so the faster run is the
 * higher score.
 *
 * Built from maintained modules — the scroll track (Blue Max's auto-scroll
 * camera, here a straight descent paced by the skier), vitals and contact
 * damage for the wipeout, the debris burst for the snow puff, the static
 * body, ballistic shards and the objective — plus the two downhill modules
 * (the carving body and the timed course). */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { RouteSector, ScrollTrackConfig } from '../modules/strike.js';
import type { TemplateV1 } from './template.js';

const W = 9, H = 16;
/** The skier's centre: mid-slope, in the upper quarter of the view. */
const ANCHOR = { x: 4.5, y: 4 };
const SKIER = { w: 0.7, h: 0.9 };
/** The racer's pace scalar the course scrolls at. */
const PACE = 'ski.pace';
const TICKS = 30;

/** A slalom gate placed `at` a fraction of its section, `across` the line. */
const gate = (at: number, across: number) => ({ archetype: 'slalom-gate', at, jitter: 1.5, across, escorts: [] });
const trees = (from: number, to: number, everyMin: number, everyMax: number, acrossMin: number, acrossMax: number) =>
  ({ archetype: 'pine-tree', from, to, everyMin, everyMax, acrossMin, acrossMax });
const moguls = (from: number, to: number, everyMin: number, everyMax: number, acrossMin: number, acrossMax: number) =>
  ({ archetype: 'mogul', from, to, everyMin, everyMax, acrossMin, acrossMax });

const sectors: RouteSector[] = [
  // The open speed field: the first decision is a pair of pines across the
  // line — the narrow gap between them runs straight (fast), the sweep
  // around them is safe but costs pace; then sparse trees and wide gates.
  { length: 100, marks: [
    { archetype: 'pine-tree', at: 0.2, jitter: 1, across: -0.9, escorts: [{ archetype: 'pine-tree', along: 0, across: 2.1 }] },
    gate(0.42, -1.8), gate(0.64, 1.8), gate(0.86, -1.4),
  ], waves: [trees(0.3, 1, 6, 10, -4, 4), moguls(0.3, 1, 10, 16, -3.5, 3.5)] },
  // The tree corridor: dense pines down both edges, a few in the channel,
  // gates close to the line — speed is easy to gain and hard to steer.
  { length: 110, marks: [gate(0.12, 1.2), gate(0.32, -1.2), gate(0.52, 1.2), gate(0.72, -1.2), gate(0.92, 1)], waves: [
    trees(0, 1, 1.6, 2.6, -4.2, -2.6), trees(0, 1, 1.6, 2.6, 2.6, 4.2), trees(0.05, 0.95, 9, 14, -1.6, 1.6),
  ] },
  // The mogul field: bumps everywhere, sparse trees, each gate just past a band of bumps.
  { length: 100, marks: [gate(0.2, 2), gate(0.42, -2), gate(0.64, 2), gate(0.86, -1.6)], waves: [
    moguls(0, 1, 1.8, 3, -3.6, 3.6), trees(0.05, 0.95, 8, 13, -4, 4),
  ] },
];

const track: ScrollTrackConfig = { heading: 180, speed: 1, speedScalar: PACE, anchorTag: 'ground', anchor: ANCHOR, margin: 1,
  safeAlong: 14, markGap: 12, markClear: 2.5, sectors, finale: { archetype: 'finish-banner', across: 0 } };

const globals: ModuleUseV1[] = [
  { use: 'world.scrollTrack', config: track },
  // Pines are a wipeout (the skier is down for the tumble, then recovers where it fell).
  { use: 'rule.contactDamage', config: { attackerTag: 'tree', victimTag: 'skier', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: false } },
  // Half the twelve gates missed is a disqualification (the no-input run's defeat).
  { use: 'rule.course', config: { gateTag: 'gate', racerTag: 'skier', inset: 0.25, gatePoints: 100, penaltyTicks: 2 * TICKS, disqualifyAfter: 6, finishOn: 'route-end',
    progressScalar: 'route.progress', parTicks: 100 * TICKS, pointsPerSecond: 100 } },
  { use: 'fx.debris', config: { on: 'damage', fromTag: 'skier', archetype: 'snow-spray', count: 6, speed: 3 } },
  { use: 'flow.objective', config: { win: 'goal', count: 0, milestone: 0, lose: 'players-out', requiredTag: 'gate', collectTag: 'gate', celebrate: true } },
];

export const SKI_2600_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Ski 2600',
  summary: 'a retro top-down downhill slalom skiing race down a scrolling snowy course',
  world: { w: W, h: H, orientation: 'portrait' },
  camera: { mode: 'auto-scroll' },
  players: { archetypes: ['skier'], spawns: [{ x: ANCHOR.x, y: ANCHOR.y + SKIER.h / 2, facing: 1 }] },
  archetypes: {
    skier: {
      role: 'a small chunky skier seen from behind and above in an orange jacket and blue helmet, skis pointing down the slope',
      tags: ['player', 'skier'], size: SKIER, render: { palette: 7, depth: 50, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.carve', config: { tuck: 9, cruise: 6.5, plow: 3, accel: 5, brake: 9, turnRate: 210, recenter: 140, maxAngle: 75, attitude: 0.8,
          band: { left: 0.3, right: 0.3 }, paceScalar: PACE, bumpTag: 'mogul', bumpSlow: 0.55, bumpShove: 3, bumpTicks: 10, recoverInPlace: true } },
        // Nine wipeouts allowed: the tumble pose, then recovery in place with
        // a moment to clear the tree.
        { use: 'core.vitals', config: { lives: 9, hurtTicks: 45, respawnTicks: 0, invulnTicks: 40 } },
      ],
    },
    'pine-tree': {
      role: 'a squat evergreen pine of dark green stacked triangle tiers on a short brown trunk, standing in snow',
      tags: ['ground', 'tree'], size: { w: 1, h: 1.3 }, render: { palette: 3, depth: 20, anchor: 'bottom', facing: 'front' },
      modules: [{ use: 'body.static', config: {} }],
    },
    mogul: {
      role: 'a rounded white snow mogul bump with a cool blue shaded lower lip and a bright highlight on top',
      tags: ['ground', 'mogul'], size: { w: 1.1, h: 0.6 }, render: { palette: 1, depth: 10, anchor: 'bottom', facing: 'symmetric' },
      modules: [{ use: 'body.static', config: {} }],
    },
    'slalom-gate': {
      role: 'a slalom gate: a red pole with a pennant on the left and a blue pole with a pennant on the right, open snow between them',
      tags: ['ground', 'gate'], size: { w: 2.4, h: 1.1 }, render: { palette: 5, depth: 15, anchor: 'bottom', facing: 'front' },
      modules: [{ use: 'body.static', config: {} }],
    },
    'finish-banner': {
      role: 'a wide black-and-white checkered finish banner with red trim strung between two posts planted in snow',
      tags: ['ground'], size: { w: 8.6, h: 1.6 }, render: { palette: 4, depth: 12, anchor: 'bottom', facing: 'front', label: 'FINISH' },
      modules: [{ use: 'body.static', config: {} }],
    },
    'snow-spray': {
      role: 'a tiny puff of chunky white snow flecks with pale blue shading',
      tags: ['spray'], size: { w: 0.3, h: 0.3 }, render: { palette: 2, depth: 55, anchor: 'center', facing: 'symmetric' },
      modules: [{ use: 'body.ballistic', config: { gravity: 6, lifeTicks: 20, landOnSurfaces: false, leaveTag: 'spray' } }],
    },
  },
  globals,
  // The concept's HUD: the running race time (penalties included), gates
  // threaded and missed, and the distance left to the finish — after the score.
  hud: [{ from: 'score', field: 'score' }, { from: 'rule.course', field: 'time' }, { from: 'rule.course', field: 'gates' }, { from: 'rule.course', field: 'missed' },
    { from: 'world.scrollTrack', field: 'distance', label: 'TO GO' }],
  input: { profile: 'move4-fire', labels: { a: 'TUCK' } },
  presentation: {
    projection: 'top-down',
    backgroundRole: 'an open sunlit snow slope with pale blue shading bands, faint groomed corduroy lines and distant blue peaks',
    palette: ['#f4fbff', '#bcd8ee', '#7aa7cc', '#1e6b3a', '#5a3a20', '#d8322a', '#2a4fd8', '#f08a1e'],
    backgroundPaletteIndex: 0,
  },
};

export const SKI_2600_TEMPLATE: TemplateV1 = {
  id: 'downhill/ski-2600',
  family: 'downhill',
  title: 'Ski 2600 downhill slalom',
  pitch: 'Atari-style ski race: carve down a scrolling slope through slalom gates, dodge pines and moguls, and stop the clock at the finish.',
  disclosure: {
    coreVerbs: ['steer', 'tuck', 'snowplow'],
    mechanics: [
      'The skier holds the upper part of the screen while the snowy course scrolls up past them at the speed they ski; the run never turns back.',
      'Left and right swing the skier\'s heading smoothly and immediately; releasing returns them to the fall line. The arc is tighter the slower you go, and traversing across the slope costs downhill speed.',
      'Up (or Tuck) crouches for top speed; down snowplows to shed speed for tighter turns. There is no jumping and nothing to shoot.',
      'Hitting a pine tree is a wipeout: a tumble of about a second and a half with the clock still running, then you get up where you fell at low speed. A ninth wipeout ends the run.',
      'Clipping a mogul is a lesser bump: you are shoved sideways and lose speed for a moment without falling.',
      'Slalom gates are a red and a blue pole: pass between them for a chime and 100 points; missing one adds two seconds to your time, and missing six of the twelve disqualifies the run.',
      'The course is three fixed sections after an obstacle-free drop-in: an open speed field that opens on a narrow gap between two pines (fast) or a wide sweep around them (safe), a tight tree corridor that punishes over-tucking, and a mogul field with gates just past the bumps.',
      'Trees and moguls are reshuffled within each section every run, never onto a gate\'s line; obstacles only enter from the bottom of the screen and there is always a way through.',
      'The HUD shows the running race time with penalties, gates passed and missed, and the distance to the finish.',
    ],
    controls: 'Left/right steer; up or Tuck tucks for speed; down snowplows to slow down.',
    winCondition: 'Reach the finish banner at the bottom of the course. Your race time is the clock plus two seconds per missed gate; each gate threaded scores 100 and every second under par scores 100 more, so a faster, cleaner run scores higher. Missing six gates, or a ninth wipeout, ends the run without a finish.',
  },
  music: { mood: 'bright crisp sunny alpine morning rush', genre: 'arcade chiptune', tempoBpm: 150,
    instrumentation: ['square lead', 'triangle bass', 'noise hi-hat', 'pulse arpeggio'] },
  spec: SKI_2600_SPEC,
  tunables: [
    { path: '/archetypes/skier/modules/body.carve/tuck', min: 8, max: 11 },
    { path: '/archetypes/skier/modules/body.carve/cruise', min: 5, max: 8 },
    { path: '/archetypes/skier/modules/body.carve/turnRate', min: 140, max: 280 },
    { path: '/archetypes/skier/modules/body.carve/bumpSlow', min: 0.35, max: 0.8 },
    { path: '/archetypes/skier/modules/core.vitals/hurtTicks', min: 30, max: 60, integer: true },
    { path: '/globals/rule.course/penaltyTicks', min: 0, max: 150, integer: true },
    { path: '/globals/rule.course/parTicks', min: 1800, max: 4800, integer: true },
    { path: '/globals/rule.course/disqualifyAfter', min: 4, max: 9, integer: true },
    { path: '/globals/world.scrollTrack/sectors/0/length', min: 80, max: 140 },
    { path: '/globals/world.scrollTrack/sectors/1/length', min: 90, max: 150 },
    { path: '/globals/world.scrollTrack/sectors/2/length', min: 80, max: 140 },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  // Every run carves, tucks, threads gates and crosses the finish; whether a seed's
  // trees ever catch the solver is play, not proof.
  requirements: { events: ['carve', 'tuck', 'gate', 'finish'] },
  signatureModules: ['body.carve', 'world.scrollTrack', 'rule.course'],
  // The skier starts at rest on the drop-in: steering swings it across the
  // slope as it picks up speed, up tucks, down snowplows.
  verbProofs: [
    { verb: 'steer', input: 'right', ms: 600, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'tuck', input: 'up', ms: 300, expect: { kind: 'event', event: 'tuck' } },
    { verb: 'snowplow', input: 'down', ms: 300, expect: { kind: 'event', event: 'snowplow' } },
  ],
};
